import React, { useState, useMemo } from 'react';
import { useGetAccountBalancesByCategory } from '../../api/accounts.js';
import { useGetDRFAIncomeExpense } from '../../api/drfa_income_expense.js';
import { useGetCaseDetails } from '../../api/casedetails';
import { useGetEnrichedTransactions, flattenInfiniteData } from '../../api/transactions.js';
import { formatCurrency } from '../../utils/formatters.js';
import ExportDisplayOptionsForm from './ExportDisplayOptionsForm.js';
import { buildEstateWorkbook, getDefaultFileName, downloadWorkbook } from './estate-summary-excel-builder.js';
import PageSkeleton from './pageskeleton.js';

import { PageHeader, SectionTitle, Card, Badge } from '../../design-system';
import styles from './estate-summary-export.module.css';

const FileDownload = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21,15V19a2,2,0,0,1-2,2H5a2,2,0,0,1-2-2V15"></path>
    <polyline points="7,10 12,15 17,10"></polyline>
    <line x1="12" y1="15" x2="12" y2="3"></line>
  </svg>
);

const TableIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
    <line x1="3" y1="9" x2="21" y2="9"></line>
    <line x1="3" y1="15" x2="21" y2="15"></line>
    <line x1="9" y1="3" x2="9" y2="21"></line>
  </svg>
);

const EstateSummaryExport = () => {
  const { data: assetLiabilityData, isLoading: alLoading } = useGetAccountBalancesByCategory();
  const { data: drfaData, isLoading: drfaLoading } = useGetDRFAIncomeExpense();
  const { data: caseDetails, isLoading: cdLoading } = useGetCaseDetails();

  const [reportForClient, setReportForClient] = useState(true);
  const [exporting, setExporting] = useState(false);

  const [displayOptions, setDisplayOptions] = useState({
    showNotes: true,
    showSourceColumns: true,
    showSettlementColumns: true,
    showSupportFields: false,
  });

  const allSheets = ['Title', 'Personal Info', 'Income', 'Expenses', 'Assets', 'Liabilities', 'Transactions'];
  const [selectedSheets, setSelectedSheets] = useState(new Set(allSheets));

  // Only pull the (potentially large) enriched-transaction feed when the
  // Transactions sheet is actually selected for export.
  const includeTransactions = selectedSheets.has('Transactions');
  const { data: txnPages, isLoading: txnLoading } = useGetEnrichedTransactions(includeTransactions);
  const transactions = useMemo(() => flattenInfiniteData(txnPages), [txnPages]);

  const toggleSheet = (name) => {
    setSelectedSheets(prev => {
      const next = new Set(prev);
      if (next.has(name)) {
        if (next.size > 1) next.delete(name);
      } else {
        next.add(name);
      }
      return next;
    });
  };

  const isLoading = alLoading || drfaLoading || cdLoading;

  if (isLoading) {
    return <PageSkeleton />;
  }

  const incomeItems = (drfaData || []).filter(item => {
    const t = (item.type || item.category || '').toLowerCase();
    return t.includes('income');
  });
  const expenseItems = (drfaData || []).filter(item => {
    const t = (item.type || item.category || '').toLowerCase();
    return t.includes('expense');
  });

  const totalIncome = incomeItems.reduce((s, i) => s + (i.monthly_average_amount || 0), 0);
  const totalExpenses = expenseItems.reduce((s, i) => s + (i.monthly_average_amount || 0), 0);

  // Net asset values — include associated liabilities (same as DRFA's calculateAccountNetValue)
  const calculateAccountNetValue = (account) => {
    let netValue = account.latest_balance?.end_balance || 0;
    if (account.associated_liabilities) {
      netValue += account.associated_liabilities.reduce(
        (s, liab) => s + (liab.latest_balance?.end_balance || 0), 0
      );
    }
    return netValue;
  };

  const totalAssets = assetLiabilityData?.assets
    ? Object.values(assetLiabilityData.assets).reduce((sum, section) => {
        return sum + (section.accounts || []).reduce((s, a) => s + calculateAccountNetValue(a), 0);
      }, 0)
    : 0;

  const totalLiabilities = assetLiabilityData?.liabilities
    ? Object.values(assetLiabilityData.liabilities).reduce((sum, section) => {
        return sum + (section.accounts || []).reduce((s, a) => s + calculateAccountNetValue(a), 0);
      }, 0)
    : 0;

  const handleExport = async () => {
    setExporting(true);
    try {
      const wb = buildEstateWorkbook({
        caseDetails,
        drfaData,
        assetLiabilityData,
        transactions,
        displayOptions,
        reportForClient,
        selectedSheets,
      });

      await downloadWorkbook(wb, getDefaultFileName(caseDetails));
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Export failed:', err);
      }
    } finally {
      setExporting(false);
    }
  };

  const headerSubtitle = (
    <span>
      Export case financial data as a multi-tab Excel workbook for custom reporting.
    </span>
  );

  const headerActions = (
    <div className={styles.headerActions}>
      {caseDetails?.caseNumber && (
        <Badge variant="info" size="md">Case #: {caseDetails.caseNumber}</Badge>
      )}
      <div className={styles.reportToggle}>
        <span className={styles.reportToggleLabel}>Report for:</span>
        <span className={`${styles.reportToggleOption} ${!reportForClient ? styles.reportToggleOptionActive : ''}`}>
          Spouse
        </span>
        <button
          type="button"
          onClick={() => setReportForClient(!reportForClient)}
          className={`${styles.reportToggleSwitch} ${reportForClient ? styles.reportToggleSwitchOn : ''}`}
          aria-label={`Report is for ${reportForClient ? 'Client' : 'Spouse'}. Click to switch.`}
        >
          <span className={`${styles.reportToggleKnob} ${reportForClient ? styles.reportToggleKnobOn : ''}`} />
        </button>
        <span className={`${styles.reportToggleOption} ${reportForClient ? styles.reportToggleOptionActive : ''}`}>
          Client
        </span>
      </div>
      <button
        type="button"
        onClick={handleExport}
        className={styles.exportButton}
        disabled={exporting || (includeTransactions && txnLoading)}
        aria-label="Export to Excel"
      >
        <FileDownload />
        {exporting
          ? 'Exporting...'
          : (includeTransactions && txnLoading ? 'Loading transactions...' : 'Export to Excel')}
      </button>
    </div>
  );

  return (
    <div className={styles.page}>
      <PageHeader
        title="Estate Summary Export"
        subtitle={headerSubtitle}
        actions={headerActions}
      />

      <section className={styles.sectionGap}>
        <Card padding="md">
          <ExportDisplayOptionsForm
            displayOptions={displayOptions}
            setDisplayOptions={setDisplayOptions}
          />
        </Card>
      </section>

      <section className={styles.sectionGap}>
        <SectionTitle title="Export Preview" />
        <Card padding="md">
          <div className={styles.previewGrid}>
            <div className={styles.previewItem}>
              <span className={styles.previewLabel}>Monthly Income</span>
              <span className={styles.previewValue}>{formatCurrency(totalIncome)}</span>
              <span className={styles.previewDetail}>{incomeItems.length} categories</span>
            </div>
            <div className={styles.previewItem}>
              <span className={styles.previewLabel}>Monthly Expenses</span>
              <span className={styles.previewValue}>{formatCurrency(totalExpenses)}</span>
              <span className={styles.previewDetail}>{expenseItems.length} categories</span>
            </div>
            <div className={styles.previewItem}>
              <span className={styles.previewLabel}>Total Assets</span>
              <span className={styles.previewValue}>{formatCurrency(totalAssets)}</span>
            </div>
            <div className={styles.previewItem}>
              <span className={styles.previewLabel}>Total Liabilities</span>
              <span className={styles.previewValue}>{formatCurrency(totalLiabilities)}</span>
            </div>
            <div className={styles.previewItem}>
              <span className={styles.previewLabel}>Net Worth</span>
              <span className={styles.previewValue}>{formatCurrency(totalAssets + totalLiabilities)}</span>
            </div>
          </div>
        </Card>
      </section>

      <section className={styles.sectionGap}>
        <SectionTitle title="Include Sheets" />
        <Card padding="md">
          <p className={styles.sheetHint}>Click to include or exclude sheets from the workbook.</p>
          <div className={styles.sheetList}>
            {allSheets.map(name => {
              const isSelected = selectedSheets.has(name);
              return (
                <button
                  key={name}
                  type="button"
                  onClick={() => toggleSheet(name)}
                  className={`${styles.sheetBadge} ${isSelected ? styles.sheetBadgeSelected : styles.sheetBadgeUnselected}`}
                  aria-pressed={isSelected}
                  aria-label={`${isSelected ? 'Exclude' : 'Include'} ${name} sheet`}
                >
                  <span className={styles.sheetBadgeIcon}><TableIcon /></span>
                  {name}
                </button>
              );
            })}
          </div>
        </Card>
      </section>
    </div>
  );
};

export default EstateSummaryExport;
